import { motion, type Variants } from "motion/react";
import type { CSSProperties } from "react";

import bevHendry from "@/assets/people/bev-hendry.jpg";
import markTabrett from "@/assets/people/mark-tabrett.jpg";
import { NoiseTexture } from "@/components/magicui/noise-texture";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

const EASE = [0.25, 0.1, 0.25, 1] as const;

const headerVariants: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.05 } },
};

const headerItemVariants: Variants = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};

const cardsVariants: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.14, delayChildren: 0.1 } },
};

const cardVariants: Variants = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};

const viewport = { once: true, margin: "-80px" } as const;

type Person = {
  bio: string;
  name: string;
  role: string;
  photo: string;
  emphasis: "primary" | "surface";
};

const PEOPLE: Person[] = [
  {
    bio: m.landing_leadership_founder_bio(),
    name: "Mark Tabrett",
    role: m.landing_leadership_founder_role(),
    photo: markTabrett,
    emphasis: "primary",
  },
  {
    bio: m.landing_leadership_advisor_bio(),
    name: "Bev Hendry",
    role: m.landing_leadership_advisor_role(),
    photo: bevHendry,
    emphasis: "surface",
  },
];

/**
 * Leadership and advisory cards. Reuses the landing's large card treatment:
 * the founder card anchors the row on `bg-primary`, the advisory card sits on
 * the tinted surface variant. Portraits come from `assets/people/`.
 */
export const LeadershipSection = () => {
  return (
    <section
      id="leadership"
      data-tour="landing-leadership"
      className="mx-auto w-full max-w-[1100px] scroll-mt-20 px-5 py-12 md:py-16"
    >
      <motion.div
        className="mx-auto flex max-w-2xl flex-col items-center gap-4 text-center"
        variants={headerVariants}
        initial="hidden"
        whileInView="show"
        viewport={viewport}
      >
        <motion.h2
          variants={headerItemVariants}
          className="text-3xl font-semibold leading-tight text-foreground md:text-4xl"
        >
          {m.landing_leadership_title()}
        </motion.h2>
        <motion.p
          variants={headerItemVariants}
          className="max-w-xl text-sm font-medium text-muted-foreground md:text-base"
        >
          {m.landing_leadership_subtitle()}
        </motion.p>
      </motion.div>

      <motion.div
        className="mt-12 flex flex-col gap-4 md:flex-row md:items-stretch md:gap-6"
        variants={cardsVariants}
        initial="hidden"
        whileInView="show"
        viewport={viewport}
      >
        {PEOPLE.map((person) => (
          <motion.div key={person.name} variants={cardVariants} className="flex flex-1">
            <PersonCard person={person} />
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
};

function PersonCard({ person }: { person: Person }) {
  const isPrimary = person.emphasis === "primary";

  const surface = isPrimary
    ? "bg-gradient-to-br from-primary to-primary/85 text-primary-foreground"
    : "bg-gradient-to-b from-[color-mix(in_oklch,var(--primary)_22%,var(--card))] to-[color-mix(in_oklch,var(--primary)_10%,var(--background))] text-foreground outline outline-1 outline-border backdrop-blur-sm";

  // Bright top-left highlight with a faint bottom-right one, built from tokens
  // so it tracks the theme. Full strength on the founder card, faded on the
  // advisory card.
  const glowStyle: CSSProperties = {
    background:
      "radial-gradient(115% 95% at 0% 0%, color-mix(in oklch, var(--primary-foreground) 42%, transparent), transparent 55%), radial-gradient(80% 80% at 100% 100%, color-mix(in oklch, var(--primary-foreground) 16%, transparent), transparent 60%)",
    opacity: isPrimary ? 1 : 0.3,
  };

  return (
    <div
      className={cn(
        "relative isolate flex w-full flex-col justify-between gap-6 overflow-hidden rounded-[10px] p-6 md:min-h-[320px]",
        surface,
      )}
    >
      <div aria-hidden className="pointer-events-none absolute inset-0 z-0" style={glowStyle} />
      <NoiseTexture
        aria-hidden
        frequency={0.9}
        slope={isPrimary ? 0.1 : 0.18}
        noiseOpacity={isPrimary ? 0.45 : 0.6}
        className={isPrimary ? "opacity-20 dark:opacity-25" : "opacity-30 dark:opacity-40"}
      />
      <p className="relative z-10 break-words text-lg font-medium leading-7 md:text-xl md:leading-8">{person.bio}</p>
      <div className="relative z-10 flex items-center gap-3">
        <img
          src={person.photo}
          alt={person.name}
          className={cn(
            "size-14 shrink-0 rounded-full object-cover ring-2",
            isPrimary ? "ring-primary-foreground/25" : "ring-primary/20",
          )}
        />
        <div className="flex flex-col">
          <span className="text-sm font-medium text-current">{person.name}</span>
          <span
            className={cn("text-sm font-normal", isPrimary ? "text-primary-foreground/70" : "text-muted-foreground")}
          >
            {person.role}
          </span>
        </div>
      </div>
    </div>
  );
}
